import React, { useCallback, useState } from "react";

import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import { Modal } from "@mapmycustomers/ui";

import RadioGroup from "@app/component/input/RadioGroup";
import zIndices from "@app/styles/_z-index.module.scss";

import styles from "./CloseConfirmationModal.module.scss";

export interface CloseConfirmationModalProps {
  onHide: (closeMode: "cancel" | "close" | "forever") => void;
}

const CloseConfirmationModal = ({ onHide }: CloseConfirmationModalProps) => {
  const intl = useIntl();
  const [closeMode, setCloseMode] = useState<"close" | "forever">("close");

  const handleOk = useCallback(() => onHide(closeMode), [onHide, closeMode]);
  const handleCancel = useCallback(() => onHide("cancel"), [onHide]);

  return (
    <Modal
      centered
      className={styles.container}
      okText={intl.formatMessage({
        id: "guides.closeConfirmation.confirm",
        defaultMessage: "Confirm",
        description:
          "Ok button caption in a Close Confirmation modal shown when user closes a Checklist flow",
      })}
      onCancel={handleCancel}
      onOk={handleOk}
      open
      title={intl.formatMessage(
        {
          id: "guides.closeConfirmation.title",
          defaultMessage: "{icon} Dismiss for now or forever?",
          description: "Title of a Close Confirmation modal",
        },
        {
          icon: <FontAwesomeIcon className={styles.icon} icon={faQuestionCircle} />,
        }
      )}
      width={400}
      // Shared guide/flow z-index scale; sits one layer above the tour overlay.
      zIndex={Number(zIndices.flowCloseConfirmation)}
    >
      <RadioGroup className={styles.radioGroup} onChange={setCloseMode} value={closeMode}>
        <Space direction="vertical">
          <Radio key="close" value="close">
            {intl.formatMessage({
              id: "guides.closeConfirmation.options.dismissForNow",
              defaultMessage: "Dismiss for now",
              description: "Dismiss for now option in a Close Confirmation modal",
            })}
          </Radio>
          <Radio key="forever" value="forever">
            {intl.formatMessage({
              id: "guides.closeConfirmation.options.dismissForever",
              defaultMessage: "Dismiss forever",
              description: "Dismiss forever option in a Close Confirmation modal",
            })}
          </Radio>
        </Space>
      </RadioGroup>
    </Modal>
  );
};

export default CloseConfirmationModal;
